import { lazy, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useSmoothScroll,
  unlockHeroRefresh,
  scheduleRefresh,
  useIdleMounted,
} from '../components/cinematic/core';
import CineNav from '../components/cinematic/CineNav';
import SiteFooter from '../components/cinematic/SiteFooter';
import EarthSequence from '../components/cinematic/EarthSequence';
import Interlude from '../components/cinematic/Interlude';
import { SCENES } from '../components/cinematic/media';
import { Seo } from '../components/cinematic/Seo';

// Below-the-fold chapters are code-split & lazy-loaded.
const StoryStackSection = lazy(() => import('../components/cinematic/StoryStackSection'));
const ItinerarySection = lazy(() => import('../components/cinematic/ItinerarySection'));
const JourneySection = lazy(() => import('../components/cinematic/JourneySection'));
const AISection = lazy(() => import('../components/cinematic/AISection'));
const CollabSection = lazy(() => import('../components/cinematic/CollabSection'));
const MemoriesSection = lazy(() => import('../components/cinematic/MemoriesSection'));
const StatsSection = lazy(() => import('../components/cinematic/StatsSection'));
const OfflineSection = lazy(() => import('../components/cinematic/OfflineSection'));
const PricingSection = lazy(() => import('../components/cinematic/PricingSection'));
const FinalCTA = lazy(() => import('../components/cinematic/FinalCTA'));

const Fallback = () => <div style={{ minHeight: '100vh' }} aria-hidden />;

export default function LandingPage() {
  useSmoothScroll();
  // Every lazy chapter below shares one Suspense boundary, so React kicks
  // off all their dynamic imports the instant it's rendered — not when
  // each scrolls into view. Idle-gating the whole boundary keeps that
  // burst of network requests from competing with the hero's own critical
  // resources during the LCP window (mirrors EarthCanvas's own gate).
  const belowFoldReady = useIdleMounted();

  useEffect(() => {
    // Backup unlock if the hero effect never completes (e.g. reduced-motion race).
    const t = window.setTimeout(() => unlockHeroRefresh(100), 3500);
    return () => window.clearTimeout(t);
  }, []);

  // Below-fold chapters mount after the hero idle gate — that swap replaces a
  // 100vh placeholder with the real stack, so ScrollTrigger start/end must
  // recalculate or section reveals stay dead. Use scheduleRefresh (not unlock)
  // so we never force-open the gate mid-hero entrance.
  useEffect(() => {
    if (!belowFoldReady) return;
    scheduleRefresh(650);
  }, [belowFoldReady]);

  // A hash link (nav/footer "Pricing" etc.) is a client-side route change —
  // the browser only does its native hash-scroll on a real page load, and
  // this also covers clicking the same link again while already on `/`.
  // Below-fold sections are lazy + idle-gated, so the target may not exist
  // yet; poll briefly. Depends on `hash` so it re-fires on every such click.
  const { hash } = useLocation();
  useEffect(() => {
    if (!belowFoldReady || !hash) return;
    let tries = 0;
    const id = window.setInterval(() => {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.clearInterval(id);
      } else if (++tries > 30) {
        window.clearInterval(id);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, [belowFoldReady, hash]);

  return (
    <div className="cine">
      <Seo
        title="Rovera — AI Travel Planner & Shared Trip Itinerary"
        description="Rovera is the AI travel planner that brings flights, hotels, offline maps and expense tracking into one shared itinerary. Plan group trips, split costs, and keep every travel memory in one place."
        path="/"
      />
      <CineNav />
      <main>
        {/* Ch 1–2 · The Beginning / Discovery — Earth emerges from darkness */}
        <EarthSequence />
        {!belowFoldReady && <Fallback />}
        {belowFoldReady && (
        <Suspense fallback={<Fallback />}>
          {/* Ch 2.5 · Why Rovera — the scattered stack becomes one place */}
          <StoryStackSection />
          {/* Ch 3 · Dream — filmed interlude */}
          <Interlude
            scene={SCENES.plan}
            label="Plan together"
            title={
              <>
                Plan it <span className="cine-grad-text">together.</span>
              </>
            }
            sub="Everyone’s ideas. One itinerary."
          />
          {/* Ch 4 · AI — the trip assembles itself */}
          <ItinerarySection />
          <AISection />
          {/* Ch 5 · Together — realtime collaboration */}
          <CollabSection />
          {/* Ch 6 · The Journey — filmed interlude, then the world opens up */}
          <Interlude
            scene={SCENES.explore}
            label="Explore the world"
            title={
              <>
                Go <span className="cine-grad-text">further.</span>
              </>
            }
            sub="The world is bigger than your bucket list."
          />
          <JourneySection />
          {/* Ch 7 · Memory */}
          <Interlude
            scene={SCENES.memories}
            label="Memories"
            title={
              <>
                Some trips <span className="cine-grad-text">never end.</span>
              </>
            }
            sub="Photos. Voices. Words. Kept alive."
          />
          <MemoriesSection />
          {/* Ch 8 · Statistics — data becomes art */}
          <StatsSection />
          {/* Ch 9 · Offline */}
          <OfflineSection />
          {/* Ch 11 · Premium */}
          <PricingSection />
          {/* Ch 12 · Finale */}
          <FinalCTA />
        </Suspense>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
